import React, { useEffect, useMemo, useState, useRef, Fragment } from 'react';
import { TrendingUp, TrendingDown, Leaf, AlertTriangle, Sparkles, Search, Activity, ArrowUpRight, FileText, Download, Code2, GitBranch, RefreshCw, Layers, Zap, CheckCircle2, Play, Clock, AlertCircle } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { MiniSparkline } from '../components/charts/MiniSparkline';
// --- MOCK DATA ---
const generateDailyEmissions = () => {
  const data = [];
  const today = new Date();
  for (let i = 30; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    data.push({
      date: date.toLocaleDateString('it-IT', {
        day: '2-digit',
        month: 'short'
      }),
      OpenAI: Number((3 + Math.random() * 1.5).toFixed(2)),
      Anthropic: Number((1.5 + Math.random() * 1).toFixed(2)),
      Google: Number((0.3 + Math.random() * 0.5).toFixed(2))
    });
  }
  return data;
};
const DAILY_EMISSIONS = generateDailyEmissions();
const SERVICES_IMPACT = [{
  id: 's1',
  name: 'Customer Assistant',
  provider: 'OpenAI',
  model: 'GPT-4o',
  impact: 42,
  percent: 33.9,
  trend: [40, 42, 41, 43, 45, 44, 42],
  status: 'warning'
}, {
  id: 's2',
  name: 'Resume Screener',
  provider: 'OpenAI',
  model: 'GPT-4',
  impact: 31,
  percent: 25.0,
  trend: [20, 22, 25, 28, 30, 32, 31],
  status: 'danger'
}, {
  id: 's3',
  name: 'Smart Matcher',
  provider: 'Anthropic',
  model: 'Claude 3.5 Sonnet',
  impact: 22,
  percent: 17.7,
  trend: [25, 24, 23, 22, 21, 22, 22],
  status: 'success'
}, {
  id: 's4',
  name: 'Document Analyzer',
  provider: 'Google',
  model: 'Gemini Pro',
  impact: 16,
  percent: 12.9,
  trend: [15, 16, 15, 16, 17, 16, 16],
  status: 'success'
}, {
  id: 's5',
  name: 'Content Moderator',
  provider: 'OpenAI',
  model: 'GPT-4o-mini',
  impact: 8,
  percent: 6.5,
  trend: [10, 9, 8, 9, 8, 7, 8],
  status: 'success'
}];
const EFFICIENCY_DATA = [{
  model: 'GPT-4o-mini',
  provider: 'OpenAI',
  consumption: '0,15 Wh',
  co2: '0,07 kg',
  efficiency: 'Ottima',
  status: 'success'
}, {
  model: 'GPT-4o',
  provider: 'OpenAI',
  consumption: '0,30 Wh',
  co2: '0,14 kg',
  efficiency: 'Buona',
  status: 'success'
}, {
  model: 'Claude 3.5 Sonnet',
  provider: 'Anthropic',
  consumption: '0,35 Wh',
  co2: '0,16 kg',
  efficiency: 'Media',
  status: 'warning'
}, {
  model: 'Gemini Pro',
  provider: 'Google',
  consumption: '0,40 Wh',
  co2: '0,18 kg',
  efficiency: 'Media',
  status: 'warning'
}, {
  model: 'GPT-4',
  provider: 'OpenAI',
  consumption: '1,20 Wh',
  co2: '0,55 kg',
  efficiency: 'Bassa',
  status: 'danger'
}];
const PROVIDER_IMPACT = [{
  name: 'OpenAI',
  color: '#00B4D8',
  co2: 81,
  percent: 65.3,
  models: 3,
  topModel: 'GPT-4o',
  trend: '-12%'
}, {
  name: 'Anthropic',
  color: '#2A3B5C',
  co2: 22,
  percent: 17.7,
  models: 1,
  topModel: 'Claude 3.5 Sonnet',
  trend: '-8%'
}, {
  name: 'Google',
  color: '#10b981',
  co2: 16,
  percent: 12.9,
  models: 1,
  topModel: 'Gemini Pro',
  trend: '-3%'
}];
const SCAN_HISTORY = [{
  id: 'sc1',
  service: 'Customer Assistant',
  repo: 'cust-assist-ai',
  date: '26 Mar 2026',
  high: 1,
  medium: 2,
  low: 2,
  co2Savings: '~18 kg',
  status: 'Da risolvere'
}, {
  id: 'sc2',
  service: 'Resume Screener',
  repo: 'resume-screen-v3',
  date: '24 Mar 2026',
  high: 2,
  medium: 1,
  low: 1,
  co2Savings: '~12 kg',
  status: 'Da risolvere'
}, {
  id: 'sc3',
  service: 'Smart Matcher',
  repo: 'smart-matcher-core',
  date: '20 Mar 2026',
  high: 0,
  medium: 1,
  low: 2,
  co2Savings: '~4 kg',
  status: 'Completata'
}, {
  id: 'sc4',
  service: 'Document Analyzer',
  repo: 'doc-analyzer',
  date: '18 Mar 2026',
  high: 0,
  medium: 2,
  low: 3,
  co2Savings: '~7 kg',
  status: 'Da risolvere'
}, {
  id: 'sc5',
  service: 'Content Moderator',
  repo: 'content-mod-v2',
  date: '15 Mar 2026',
  high: 0,
  medium: 0,
  low: 1,
  co2Savings: '~2 kg',
  status: 'Completata'
}];
const SCAN_RESULTS = {
  service: 'Customer Assistant',
  date: new Date().toLocaleDateString('it-IT', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  }),
  duration: '1m 48s',
  filesScanned: 89,
  high: 1,
  medium: 2,
  low: 2,
  totalSavings: '~33 kg CO₂/mese',
  findings: [{
    severity: 'Alta',
    file: 'src/handlers/chat.ts',
    line: 87,
    desc: 'Il servizio effettua in media 3,2 chiamate API per sessione dove una sola basterebbe. Il modulo di intent detection viene invocato ripetutamente senza caching dei risultati intermedi.',
    rule: 'Best Practice: API Call Optimization',
    savings: '~14 kg CO₂/mese'
  }, {
    severity: 'Media',
    file: 'src/prompts/systemPrompt.ts',
    line: 23,
    desc: 'I prompt contengono istruzioni ripetute e contesto statico (~1.800 token) che potrebbe essere spostato in un template condiviso, riducendo i token per query del 40%.',
    rule: 'Best Practice: Prompt Engineering',
    savings: '~8 kg CO₂/mese'
  }, {
    severity: 'Media',
    file: 'src/services/contextBuilder.ts',
    line: 156,
    desc: 'Il contesto della conversazione include lo storico completo senza limite. Implementare una finestra di contesto ridurrebbe significativamente i token inviati.',
    rule: 'Best Practice: Context Window Management',
    savings: '~5 kg CO₂/mese'
  }, {
    severity: 'Bassa',
    file: 'src/config/models.ts',
    line: 12,
    desc: "Il fallback su GPT-4 scatta nel 12% dei casi per timeout. Aumentando il timeout da 5s a 8s si ridurrebbero i fallback al 2%, evitando l'uso del modello più pesante.",
    rule: 'Best Practice: Model Fallback Strategy',
    savings: '~4 kg CO₂/mese'
  }, {
    severity: 'Bassa',
    file: 'src/middleware/cache.ts',
    line: 45,
    desc: 'Manca caching delle risposte per query identiche. Il 15% delle richieste sono duplicati entro 1h che potrebbero essere serviti da cache.',
    rule: 'Best Practice: Response Caching',
    savings: '~2 kg CO₂/mese'
  }]
};
export function GreenOpsPage() {
  const [activeTab, setActiveTab] = useState('Panoramica');
  const [period, setPeriod] = useState('30G');
  const [searchQuery, setSearchQuery] = useState('');
  // Scan state
  const [isScanning, setIsScanning] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [scanComplete, setScanComplete] = useState(false);
  const [selectedRepo, setSelectedRepo] = useState('Customer Assistant (repo: cust-assist-ai)');
  const scanIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const startScan = () => {
    setIsScanning(true);
    setScanProgress(0);
    setScanComplete(false);
    scanIntervalRef.current = setInterval(() => {
      setScanProgress(prev => {
        if (prev >= 100) {
          if (scanIntervalRef.current) clearInterval(scanIntervalRef.current);
          setIsScanning(false);
          setScanComplete(true);
          return 100;
        }
        const increment = prev < 20 ? 2 : prev < 80 ? 4 : 2;
        return Math.min(prev + increment, 100);
      });
    }, 150);
  };
  useEffect(() => {
    return () => {
      if (scanIntervalRef.current) clearInterval(scanIntervalRef.current);
    };
  }, []);
  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'Completata':
        return <span className="bg-emerald-50 text-emerald-600 border border-emerald-100 rounded-full px-2.5 py-0.5 text-[11px] font-medium flex items-center gap-1 w-fit" data-id="element-3147">
            <CheckCircle2 className="w-3 h-3" data-id="element-3148" /> Completata
          </span>;
      case 'Da risolvere':
        return <span className="bg-amber-50 text-amber-600 border border-amber-100 rounded-full px-2.5 py-0.5 text-[11px] font-medium flex items-center gap-1 w-fit" data-id="element-3149">
            <AlertTriangle className="w-3 h-3" data-id="element-3150" /> Da risolvere
          </span>;
      default:
        return null;
    }
  };
  const filteredServices = useMemo(() => {
    return SERVICES_IMPACT.filter(s => s.name.toLowerCase().includes(searchQuery.toLowerCase()) || s.provider.toLowerCase().includes(searchQuery.toLowerCase()) || s.model.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [searchQuery]);
  return <div className="min-h-screen bg-[#F4F6F8] p-4 md:p-8" data-id="element-3151">
      {/* 1. HEADER */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8" data-id="element-3152">
        <div data-id="element-3153">
          <h1 className="text-[28px] md:text-[32px] font-semibold text-[#1A202C] tracking-tight" data-id="element-3154">
            GreenOps
          </h1>
          <p className="text-[14px] text-gray-600 mt-1" data-id="element-3155">
            Dashboard di monitoraggio dell'impatto ambientale dei servizi AI.
          </p>
        </div>
        <div className="flex items-center bg-white border border-gray-200 rounded-lg p-1 shadow-sm" data-id="element-3156">
          {['7G', '30G', '90G', '12M'].map(p => <button key={p} onClick={() => setPeriod(p)} className={`px-3 py-1.5 text-[12px] font-medium rounded-md transition-colors ${period === p ? 'bg-[#F4F6F8] text-[#2A3B5C] shadow-sm' : 'text-gray-500 hover:text-[#1A202C]'}`} data-id="element-3157">
              {p}
            </button>)}
        </div>
      </div>

      {/* 2. KPI CARDS ROW */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-id="element-3158">
        {/* Card 1 */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm flex flex-col" data-id="element-3159">
          <h3 className="text-[13px] font-medium text-gray-500 mb-3" data-id="element-3160">
            Impatto CO₂ Totale Mese
          </h3>
          <div className="flex items-baseline gap-2 mt-auto" data-id="element-3161">
            <span className="text-[28px] font-bold text-[#1A202C]" data-id="element-3162">
              124 kg CO₂
            </span>
            <span className="text-[12px] text-emerald-600 font-medium flex items-center gap-0.5" data-id="element-3163">
              <TrendingDown className="w-3.5 h-3.5" data-id="element-3164" /> -15%
            </span>
          </div>
          <p className="text-[11px] text-gray-400 mt-1" data-id="element-3165">vs mese precedente</p>
        </div>

        {/* Card 2 */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm flex flex-col" data-id="element-3166">
          <h3 className="text-[13px] font-medium text-gray-500 mb-3" data-id="element-3167">
            Impatto Medio per Query
          </h3>
          <div className="flex items-baseline gap-2 mt-auto" data-id="element-3168">
            <span className="text-[28px] font-bold text-[#1A202C]" data-id="element-3169">
              0,42 Wh
            </span>
          </div>
          <p className="text-[11px] text-gray-400 mt-1" data-id="element-3170">
            vs 0,48 Wh mese prec.
          </p>
        </div>

        {/* Card 3 */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm flex flex-col" data-id="element-3171">
          <h3 className="text-[13px] font-medium text-gray-500 mb-3" data-id="element-3172">
            Servizio più Green
          </h3>
          <div className="flex items-baseline gap-2 mt-auto flex-wrap" data-id="element-3173">
            <span className="text-[20px] font-bold text-[#1A202C] leading-tight" data-id="element-3174">
              Content Moderator
            </span>
            <span className="bg-emerald-50 text-emerald-600 border border-emerald-100 rounded-full px-2 py-0.5 text-[10px] font-medium" data-id="element-3175">
              Più efficiente
            </span>
          </div>
          <p className="text-[11px] text-gray-400 mt-1" data-id="element-3176">GPT-4o-mini</p>
        </div>

        {/* Card 4 */}
        <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm flex flex-col" data-id="element-3177">
          <h3 className="text-[13px] font-medium text-gray-500 mb-3" data-id="element-3178">
            Potenziale Risparmio
          </h3>
          <div className="mt-auto" data-id="element-3179">
            <div className="flex items-baseline gap-1 mb-2" data-id="element-3180">
              <span className="text-[28px] font-bold text-[#1A202C]" data-id="element-3181">
                -18 kg CO₂/mese
              </span>
            </div>
            <div className="w-full bg-gray-100 rounded-full h-1.5 mb-1" data-id="element-3182">
              <div className="bg-amber-500 h-1.5 rounded-full" style={{
              width: '45%'
            }} data-id="element-3183"></div>
            </div>
            <p className="text-[10px] text-gray-400 leading-tight" data-id="element-3184">
              Applicando le ottimizzazioni suggerite
            </p>
          </div>
        </div>
      </div>

      {/* 3. TABS NAVIGATION */}
      <div className="border-b-2 border-gray-300 mb-6" data-id="element-3185">
        <div className="-mb-[2px] flex items-center gap-8 overflow-x-auto custom-scrollbar" data-id="element-3186">
          {['Panoramica', 'Per Servizio', 'Per Provider', 'Code Scan'].map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`pb-3 text-[15px] font-medium transition-colors whitespace-nowrap border-b-2 ${activeTab === tab ? 'border-[#00B4D8] text-[#2A3B5C]' : 'border-transparent text-gray-500 hover:text-gray-800'}`} data-id="element-3187">
                {tab}
              </button>)}
        </div>
      </div>

      {/* 4. TAB CONTENT */}
      {activeTab === 'Panoramica' && <div className="space-y-6" data-id="element-3188">
          {/* ROW 3: Chart & Ranking */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6" data-id="element-3189">
            {/* Area Chart */}
            <div className="lg:col-span-2 bg-white border border-gray-200 rounded-xl shadow-sm p-6" data-id="element-3190">
              <div className="flex justify-between items-center mb-6" data-id="element-3191">
                <h2 className="text-[16px] font-semibold text-[#1A202C]" data-id="element-3192">
                  Trend Impatto CO₂ Mensile
                </h2>
                <div className="flex items-center gap-4 text-[11px] font-medium" data-id="element-3193">
                  <div className="flex items-center gap-1.5" data-id="element-3194">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#00B4D8]" data-id="element-3195"></div>
                    OpenAI
                  </div>
                  <div className="flex items-center gap-1.5" data-id="element-3196">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#2A3B5C]" data-id="element-3197"></div>
                    Anthropic
                  </div>
                  <div className="flex items-center gap-1.5" data-id="element-3198">
                    <div className="w-2.5 h-2.5 rounded-full bg-[#10b981]" data-id="element-3199"></div>
                    Google
                  </div>
                </div>
              </div>
              <div className="h-[360px] w-full" data-id="element-3200">
                <ResponsiveContainer width="100%" height="100%" data-id="element-3201">
                  <AreaChart data={DAILY_EMISSIONS} margin={{
                top: 10,
                right: 10,
                left: -20,
                bottom: 0
              }} data-id="element-3202">
                    <defs data-id="element-3203">
                      <linearGradient id="greenColorOpenAI" x1="0" y1="0" x2="0" y2="1" data-id="element-3204">
                        <stop offset="5%" stopColor="#00B4D8" stopOpacity={0.3} data-id="element-3205" />
                        <stop offset="95%" stopColor="#00B4D8" stopOpacity={0} data-id="element-3206" />
                      </linearGradient>
                      <linearGradient id="greenColorAnthropic" x1="0" y1="0" x2="0" y2="1" data-id="element-3207">
                        <stop offset="5%" stopColor="#2A3B5C" stopOpacity={0.3} data-id="element-3208" />
                        <stop offset="95%" stopColor="#2A3B5C" stopOpacity={0} data-id="element-3209" />
                      </linearGradient>
                      <linearGradient id="greenColorGoogle" x1="0" y1="0" x2="0" y2="1" data-id="element-3210">
                        <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} data-id="element-3211" />
                        <stop offset="95%" stopColor="#10b981" stopOpacity={0} data-id="element-3212" />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" data-id="element-3213" />
                    <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{
                  fontSize: 11,
                  fill: '#6B7280'
                }} dy={10} minTickGap={30} data-id="element-3214" />
                    <YAxis axisLine={false} tickLine={false} tick={{
                  fontSize: 11,
                  fill: '#6B7280'
                }} tickFormatter={val => `${val} kg`} domain={[0, 6]} data-id="element-3215" />
                    <Tooltip contentStyle={{
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  border: '1px solid #E5E7EB',
                  fontSize: '12px',
                  boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }} itemStyle={{
                  color: '#1A202C',
                  fontWeight: 500
                }} formatter={(value: number) => [`${value} kg CO₂`, undefined]} data-id="element-3216" />
                    <Area type="monotone" dataKey="Google" stackId="1" stroke="#10b981" fill="url(#greenColorGoogle)" strokeWidth={2} data-id="element-3217" />
                    <Area type="monotone" dataKey="Anthropic" stackId="1" stroke="#2A3B5C" fill="url(#greenColorAnthropic)" strokeWidth={2} data-id="element-3218" />
                    <Area type="monotone" dataKey="OpenAI" stackId="1" stroke="#00B4D8" fill="url(#greenColorOpenAI)" strokeWidth={2} data-id="element-3219" />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Top Services */}
            <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 flex flex-col" data-id="element-3220">
              <div className="flex justify-between items-center mb-4" data-id="element-3221">
                <h2 className="text-[14px] font-semibold text-[#1A202C]" data-id="element-3222">
                  Top Servizi per Impatto
                </h2>
                <button className="text-[11px] text-[#00B4D8] font-medium hover:underline" data-id="element-3223">
                  Vedi tutti
                </button>
              </div>
              <div className="flex-1 flex flex-col gap-0 divide-y divide-gray-100" data-id="element-3224">
                {SERVICES_IMPACT.map((service, idx) => <div key={service.id} className="flex items-center justify-between py-2.5" data-id="element-3225">
                    <div className="flex items-center gap-2.5 overflow-hidden" data-id="element-3226">
                      <span className="text-[11px] font-semibold text-gray-400 w-4 text-center shrink-0" data-id="element-3227">
                        {idx + 1}
                      </span>
                      <p className="text-[13px] font-medium text-[#1A202C] truncate" data-id="element-3228">
                        {service.name}
                      </p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0 pl-2" data-id="element-3229">
                      <span className="text-[12px] font-semibold text-[#1A202C]" data-id="element-3230">
                        {service.impact} kg
                      </span>
                      <span className="text-[10px] text-gray-400 w-10 text-right" data-id="element-3231">
                        {service.percent}%
                      </span>
                    </div>
                  </div>)}
              </div>
            </div>
          </div>

          {/* ROW 4: Efficiency Table */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-id="element-3232">
            <div className="px-6 py-5 border-b border-gray-100" data-id="element-3233">
              <h2 className="text-[16px] font-semibold text-[#1A202C]" data-id="element-3234">
                Comparazione Efficienza Modelli
              </h2>
              <p className="text-[13px] text-gray-500 mt-1" data-id="element-3235">
                Stesso task, impatto diverso
              </p>
            </div>
            <div className="overflow-x-auto" data-id="element-3236">
              <table className="w-full text-left border-collapse" data-id="element-3237">
                <thead data-id="element-3238">
                  <tr className="bg-gray-50/50 border-b border-gray-100" data-id="element-3239">
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-3240">
                      Modello
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-3241">
                      Provider
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-right" data-id="element-3242">
                      Consumo (Wh/query)
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-right" data-id="element-3243">
                      CO₂/1000 query
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-center" data-id="element-3244">
                      Efficienza
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-3245">
                  {EFFICIENCY_DATA.map((row, idx) => <tr key={idx} className="hover:bg-gray-50/50 transition-colors" data-id="element-3246">
                      <td className="px-6 py-4 text-[13px] font-medium text-[#2a3a54]" data-id="element-3247">
                        {row.model}
                      </td>
                      <td className="px-6 py-4 text-[13px] text-gray-600" data-id="element-3248">
                        {row.provider}
                      </td>
                      <td className="px-6 py-4 text-[13px] text-gray-600 text-right" data-id="element-3249">
                        {row.consumption}
                      </td>
                      <td className="px-6 py-4 text-[13px] text-gray-600 text-right" data-id="element-3250">
                        {row.co2}
                      </td>
                      <td className="px-6 py-4 text-center" data-id="element-3251">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-medium border ${row.status === 'success' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : row.status === 'warning' ? 'bg-amber-50 text-amber-600 border-amber-100' : 'bg-red-50 text-red-600 border-red-100'}`} data-id="element-3252">
                          {row.status === 'success' && '🟢 '}
                          {row.status === 'warning' && '🟡 '}
                          {row.status === 'danger' && '🔴 '}
                          {row.efficiency}
                        </span>
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>
            <div className="px-6 py-3 bg-gray-50/50 border-t border-gray-100" data-id="element-3253">
              <p className="text-[11px] text-gray-400" data-id="element-3254">
                Stime basate su benchmark pubblici. Consumo varia in base a data
                center e mix energetico regionale.
              </p>
            </div>
          </div>

          {/* ROW 6: Suggestions & Reports */}
          <h2 className="text-[16px] font-semibold text-[#1A202C] mt-8 mb-4" data-id="element-3255">
            Suggerimenti & Report
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-3256">
            {/* Card 1 */}
            <div className="bg-white border border-gray-200 rounded-xl p-5 flex items-start gap-4 shadow-sm" data-id="element-3257">
              <div className="p-2 bg-emerald-50 rounded-lg shrink-0 mt-0.5" data-id="element-3258">
                <Leaf className="w-5 h-5 text-emerald-600" data-id="element-3259" />
              </div>
              <div className="flex-1" data-id="element-3260">
                <div className="flex items-center gap-2 mb-1 flex-wrap" data-id="element-3261">
                  <h3 className="text-[14px] font-semibold text-[#1A202C]" data-id="element-3262">
                    Ottimizzazione Green
                  </h3>
                  <span className="bg-emerald-50 text-emerald-700 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide border border-emerald-100" data-id="element-3263">
                    RISPARMIO STIMATO: -31 KG CO₂/MESE
                  </span>
                </div>
                <p className="text-[13px] text-gray-600 leading-relaxed mb-4" data-id="element-3264">
                  Il servizio <strong data-id="element-3265">Resume Screener</strong> utilizza GPT-4
                  (1,20 Wh/query). Passando a <strong data-id="element-3266">GPT-4o</strong> potresti
                  ridurre l'impatto ambientale del 75% mantenendo performance
                  comparabili.
                </p>
                <button className="text-[12px] font-medium text-[#00B4D8] hover:text-[#0096b4] flex items-center gap-1" data-id="element-3267">
                  Applica modifica <ArrowUpRight className="w-3.5 h-3.5" data-id="element-3268" />
                </button>
              </div>
            </div>

            {/* Card 2 */}
            <div className="bg-white border border-gray-200 rounded-xl p-5 flex items-start gap-4 shadow-sm" data-id="element-3269">
              <div className="p-2 bg-blue-50 rounded-lg shrink-0 mt-0.5" data-id="element-3270">
                <FileText className="w-5 h-5 text-[#00B4D8]" data-id="element-3271" />
              </div>
              <div className="flex-1 flex flex-col h-full" data-id="element-3272">
                <div className="flex items-center gap-2 mb-1 flex-wrap" data-id="element-3273">
                  <h3 className="text-[14px] font-semibold text-[#1A202C]" data-id="element-3274">
                    Report Sostenibilità
                  </h3>
                  <span className="bg-blue-50 text-blue-700 text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wide border border-blue-100" data-id="element-3275">
                    PRONTO PER IL BILANCIO
                  </span>
                </div>
                <p className="text-[13px] text-gray-600 leading-relaxed mb-4" data-id="element-3276">
                  Genera il report di impatto ambientale dei tuoi servizi AI.
                  Include metriche per provider, trend mensili e comparazione
                  modelli. Disponibile in CSV e PDF.
                </p>
                <div className="mt-auto" data-id="element-3277">
                  <button className="flex items-center justify-center px-4 py-2 bg-[#2A3B5C] text-white rounded-md hover:bg-[#1E2A40] transition-colors gap-2" data-id="element-3278">
                    <span className="text-[13px] font-heading uppercase tracking-wide whitespace-nowrap" data-id="element-3279">
                      Genera Report
                    </span>
                    <ArrowUpRight className="w-3.5 h-3.5" data-id="element-3280" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>}

      {/* TAB 2: PER SERVIZIO */}
      {activeTab === 'Per Servizio' && <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden flex flex-col" data-id="element-3281">
          <div className="p-4 border-b border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-4" data-id="element-3282">
            <h2 className="text-[16px] font-semibold text-[#1A202C]" data-id="element-3283">
              Dettaglio Impatto per Servizio
            </h2>
            <div className="relative w-full sm:w-72" data-id="element-3284">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" data-id="element-3285" />
              <input type="text" placeholder="Cerca servizio, provider o modello..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg text-[13px] focus:outline-none focus:border-[#00B4D8] focus:ring-1 focus:ring-[#00B4D8] transition-all" data-id="element-3286" />
            </div>
          </div>
          <div className="overflow-x-auto" data-id="element-3287">
            <table className="w-full text-left border-collapse" data-id="element-3288">
              <thead data-id="element-3289">
                <tr className="bg-gray-50/50 border-b border-gray-100" data-id="element-3290">
                  <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-3291">
                    Servizio
                  </th>
                  <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-3292">
                    Provider & Modello
                  </th>
                  <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-right" data-id="element-3293">
                    CO₂ (kg/mese)
                  </th>
                  <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-right" data-id="element-3294">
                    % Totale
                  </th>
                  <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-center" data-id="element-3295">
                    Trend (7gg)
                  </th>
                  <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-center" data-id="element-3296">
                    Efficienza
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-id="element-3297">
                {filteredServices.map(service => <tr key={service.id} className="hover:bg-gray-50/50 transition-colors group cursor-pointer" data-id="element-3298">
                    <td className="px-6 py-4" data-id="element-3299">
                      <div className="flex items-center gap-3" data-id="element-3300">
                        <div className={`w-2 h-2 rounded-full ${service.status === 'danger' ? 'bg-red-500' : service.status === 'warning' ? 'bg-amber-500' : 'bg-emerald-500'}`} data-id="element-3301"></div>
                        <span className="text-[13px] font-medium text-[#2a3a54]" data-id="element-3302">
                          {service.name}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4" data-id="element-3303">
                      <div className="flex flex-col" data-id="element-3304">
                        <span className="text-[13px] text-[#1A202C] font-medium" data-id="element-3305">
                          {service.provider}
                        </span>
                        <span className="text-[11px] text-gray-500" data-id="element-3306">
                          {service.model}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-3307">
                      <span className="text-[13px] font-semibold text-[#1A202C]" data-id="element-3308">
                        {service.impact} kg
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right" data-id="element-3309">
                      <span className="text-[13px] text-gray-600" data-id="element-3310">
                        {service.percent}%
                      </span>
                    </td>
                    <td className="px-6 py-4 w-32" data-id="element-3311">
                      <MiniSparkline data={service.trend} color={service.status === 'danger' ? '#ef4444' : '#00B4D8'} className="h-[24px]" data-id="element-3312" />
                    </td>
                    <td className="px-6 py-4 text-center" data-id="element-3313">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[10px] font-medium border ${service.status === 'success' ? 'bg-emerald-50 text-emerald-600 border-emerald-100' : service.status === 'warning' ? 'bg-amber-50 text-amber-600 border-amber-100' : 'bg-red-50 text-red-600 border-red-100'}`} data-id="element-3314">
                        {service.status === 'success' ? 'Efficiente' : service.status === 'warning' ? 'Nella media' : 'Inefficiente'}
                      </span>
                    </td>
                  </tr>)}
                {filteredServices.length === 0 && <tr data-id="element-3315">
                    <td colSpan={6} className="px-6 py-8 text-center text-[13px] text-gray-500" data-id="element-3316">
                      Nessun servizio trovato per "{searchQuery}"
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>
        </div>}

      {/* TAB 3: PER PROVIDER */}
      {activeTab === 'Per Provider' && <div className="space-y-6" data-id="element-3317">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-3318">
            {PROVIDER_IMPACT.map(provider => <div key={provider.name} className="bg-white border border-gray-200 rounded-xl shadow-sm p-5 flex flex-col" data-id="element-3319">
                <div className="flex items-center gap-2 mb-4" data-id="element-3320">
                  <div className="w-3 h-3 rounded-full" style={{
              backgroundColor: provider.color
            }} data-id="element-3321"></div>
                  <h3 className="text-[15px] font-semibold text-[#1A202C]" data-id="element-3322">
                    {provider.name}
                  </h3>
                </div>
                <div className="mb-4" data-id="element-3323">
                  <p className="text-[24px] font-bold text-[#1A202C]" data-id="element-3324">
                    {provider.co2} kg CO₂
                  </p>
                  <p className="text-[12px] font-medium mt-1 text-emerald-500" data-id="element-3325">
                    {provider.trend} vs mese prec.
                  </p>
                </div>
                <div className="mt-auto space-y-2 pt-4 border-t border-gray-100" data-id="element-3326">
                  <div className="flex justify-between items-center" data-id="element-3327">
                    <span className="text-[12px] text-gray-500" data-id="element-3328">
                      Quota impatto
                    </span>
                    <span className="text-[12px] font-medium text-[#1A202C]" data-id="element-3329">
                      {provider.percent}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-3330">
                    <span className="text-[12px] text-gray-500" data-id="element-3331">
                      Modelli attivi
                    </span>
                    <span className="text-[12px] font-medium text-[#1A202C]" data-id="element-3332">
                      {provider.models}
                    </span>
                  </div>
                  <div className="flex justify-between items-center" data-id="element-3333">
                    <span className="text-[12px] text-gray-500" data-id="element-3334">
                      Modello top
                    </span>
                    <span className="text-[12px] font-medium text-[#1A202C]" data-id="element-3335">
                      {provider.topModel}
                    </span>
                  </div>
                </div>
              </div>)}
          </div>
        </div>}

      {/* TAB 4: CODE SCAN */}
      {activeTab === 'Code Scan' && <div className="space-y-6" data-id="element-3336">
          {/* Top Bar - Scan Controls */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-4 flex flex-col sm:flex-row items-center justify-between gap-4" data-id="element-3337">
            <div className="flex items-center gap-3 w-full sm:w-auto" data-id="element-3338">
              <div className="p-2 bg-emerald-50 rounded-lg shrink-0" data-id="element-3339">
                <Code2 className="w-5 h-5 text-emerald-600" data-id="element-3340" />
              </div>
              <div data-id="element-3341">
                <h2 className="text-[15px] font-semibold text-[#1A202C]" data-id="element-3342">
                  Analisi Codice Sorgente
                </h2>
                <p className="text-[12px] text-gray-500 mt-0.5" data-id="element-3343">
                  Scansiona i repository per individuare ottimizzazioni GreenOps
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 w-full sm:w-auto" data-id="element-3344">
              <select value={selectedRepo} onChange={e => setSelectedRepo(e.target.value)} className="flex-1 sm:w-64 bg-gray-50 border border-gray-200 text-[#1A202C] px-3 py-2.5 rounded-lg text-[13px] focus:outline-none focus:border-[#00B4D8] focus:ring-1 focus:ring-[#00B4D8]" disabled={isScanning} data-id="element-3345">
                <option data-id="element-3346">Customer Assistant (repo: cust-assist-ai)</option>
                <option data-id="element-3347">Smart Matcher (repo: smart-matcher-core)</option>
                <option data-id="element-3348">Content Moderator (repo: content-mod-v2)</option>
                <option data-id="element-3349">AI Tutor (repo: ai-tutor-platform)</option>
                <option data-id="element-3350">Resume Screener (repo: resume-screen-v3)</option>
                <option data-id="element-3351">Document Analyzer (repo: doc-analyzer)</option>
              </select>
              <button onClick={startScan} disabled={isScanning} className="flex items-center justify-center gap-2 px-6 py-2.5 bg-[#2A3B5C] text-white rounded-lg hover:bg-[#1E2A40] transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed shrink-0" data-id="element-3352">
                <Play className="w-4 h-4" data-id="element-3353" />
                <span className="text-[14px] font-medium" data-id="element-3354">
                  {isScanning ? 'Scansione in corso...' : 'Avvia Scansione'}
                </span>
              </button>
            </div>
          </div>

          {/* Scan in Progress */}
          {isScanning && <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6 shadow-sm" data-id="element-3355">
              <div className="flex justify-between items-center mb-4" data-id="element-3356">
                <div className="flex items-center gap-3" data-id="element-3357">
                  <div className="p-2 bg-emerald-100 rounded-lg" data-id="element-3358">
                    <Search className="w-5 h-5 text-emerald-600 animate-pulse" data-id="element-3359" />
                  </div>
                  <div data-id="element-3360">
                    <h3 className="text-[15px] font-semibold text-emerald-900" data-id="element-3361">
                      Scansione in corso: {selectedRepo.split(' (')[0]}
                    </h3>
                    <p className="text-[12px] text-emerald-700 mt-0.5" data-id="element-3362">
                      {scanProgress < 30 ? 'Analisi struttura del progetto e dipendenze...' : scanProgress < 60 ? 'Scansione codice sorgente per pattern di chiamate API...' : scanProgress < 85 ? 'Verifica best practice GreenOps e caching...' : 'Generazione report e stima risparmio CO₂...'}
                    </p>
                  </div>
                </div>
                <span className="text-[14px] font-bold text-emerald-700" data-id="element-3363">
                  {scanProgress}%
                </span>
              </div>
              <div className="w-full bg-emerald-200/50 rounded-full h-2" data-id="element-3364">
                <div className="bg-emerald-600 h-2 rounded-full transition-all duration-200 ease-out" style={{
            width: `${scanProgress}%`
          }} data-id="element-3365"></div>
              </div>
              <div className="flex items-center gap-4 mt-3 text-[11px] text-emerald-600" data-id="element-3366">
                <span data-id="element-3367">
                  File analizzati: {Math.floor(scanProgress * 0.89)}/89
                </span>
                <span data-id="element-3368">•</span>
                <span data-id="element-3369">Tempo trascorso: {Math.floor(scanProgress * 0.15)}s</span>
              </div>
            </div>}

          {/* Scan Complete - Results */}
          {scanComplete && <div className="space-y-4" data-id="element-3370">
              {/* Summary Card */}
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6" data-id="element-3371">
                <div className="flex flex-col sm:flex-row justify-between items-start gap-4 mb-6" data-id="element-3372">
                  <div className="flex items-center gap-3" data-id="element-3373">
                    <div className="p-2 bg-emerald-100 rounded-lg" data-id="element-3374">
                      <CheckCircle2 className="w-5 h-5 text-emerald-600" data-id="element-3375" />
                    </div>
                    <div data-id="element-3376">
                      <h3 className="text-[16px] font-semibold text-[#1A202C]" data-id="element-3377">
                        Scansione completata: {selectedRepo.split(' (')[0]}
                      </h3>
                      <p className="text-[12px] text-gray-500 mt-0.5" data-id="element-3378">
                        {SCAN_RESULTS.date} • Durata: {SCAN_RESULTS.duration} •{' '}
                        {SCAN_RESULTS.filesScanned} file analizzati
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3" data-id="element-3379">
                    <span className="bg-emerald-50 text-emerald-700 text-[11px] font-bold px-3 py-1.5 rounded-lg uppercase tracking-wide border border-emerald-100" data-id="element-3380">
                      Risparmio: {SCAN_RESULTS.totalSavings}
                    </span>
                    <button className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors text-[13px] font-medium" data-id="element-3381">
                      <Download className="w-4 h-4" data-id="element-3382" />
                      Esporta Report
                    </button>
                  </div>
                </div>

                {/* Severity Summary */}
                <div className="grid grid-cols-3 gap-4" data-id="element-3383">
                  <div className="bg-red-50 border border-red-100 rounded-lg p-4 text-center" data-id="element-3384">
                    <p className="text-[28px] font-bold text-red-600" data-id="element-3385">
                      {SCAN_RESULTS.high}
                    </p>
                    <p className="text-[12px] font-medium text-red-700 mt-1" data-id="element-3386">
                      Impatto Alto
                    </p>
                  </div>
                  <div className="bg-amber-50 border border-amber-100 rounded-lg p-4 text-center" data-id="element-3387">
                    <p className="text-[28px] font-bold text-amber-600" data-id="element-3388">
                      {SCAN_RESULTS.medium}
                    </p>
                    <p className="text-[12px] font-medium text-amber-700 mt-1" data-id="element-3389">
                      Impatto Medio
                    </p>
                  </div>
                  <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 text-center" data-id="element-3390">
                    <p className="text-[28px] font-bold text-blue-600" data-id="element-3391">
                      {SCAN_RESULTS.low}
                    </p>
                    <p className="text-[12px] font-medium text-blue-700 mt-1" data-id="element-3392">
                      Impatto Basso
                    </p>
                  </div>
                </div>
              </div>

              {/* Findings Detail */}
              <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-id="element-3393">
                <div className="px-6 py-5 border-b border-gray-100" data-id="element-3394">
                  <h2 className="text-[16px] font-semibold text-[#1A202C]" data-id="element-3395">
                    Dettaglio Findings ({SCAN_RESULTS.findings.length})
                  </h2>
                </div>
                <div className="divide-y divide-gray-100" data-id="element-3396">
                  {SCAN_RESULTS.findings.map((finding, idx) => {
              const iconBg = finding.severity === 'Alta' ? 'bg-red-50 border-red-100' : finding.severity === 'Media' ? 'bg-amber-50 border-amber-100' : 'bg-blue-50 border-blue-100';
              const badgeClass = finding.severity === 'Alta' ? 'bg-red-50 text-red-600 border-red-100' : finding.severity === 'Media' ? 'bg-amber-50 text-amber-600 border-amber-100' : 'bg-blue-50 text-blue-600 border-blue-100';
              const iconColor = finding.severity === 'Alta' ? 'text-red-500' : finding.severity === 'Media' ? 'text-amber-500' : 'text-blue-500';
              return <div key={idx} className="px-6 py-4 hover:bg-gray-50/50 transition-colors" data-id="element-3397">
                        <div className="flex items-start gap-4" data-id="element-3398">
                          <div className={`mt-0.5 p-1.5 rounded-md border shrink-0 ${iconBg}`} data-id="element-3399">
                            {finding.severity === 'Alta' ? <AlertTriangle className={`w-4 h-4 ${iconColor}`} data-id="element-3400" /> : finding.severity === 'Media' ? <AlertCircle className={`w-4 h-4 ${iconColor}`} data-id="element-3401" /> : <FileText className={`w-4 h-4 ${iconColor}`} data-id="element-3402" />}
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-3403">
                            <div className="flex items-center gap-3 mb-1 flex-wrap" data-id="element-3404">
                              <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide border ${badgeClass}`} data-id="element-3405">
                                {finding.severity}
                              </span>
                              <code className="text-[12px] bg-gray-100 text-gray-700 px-2 py-0.5 rounded font-mono border border-gray-200" data-id="element-3406">
                                {finding.file}{' '}
                                <span className="text-gray-400" data-id="element-3407">
                                  :{finding.line}
                                </span>
                              </code>
                              <span className="text-[11px] text-emerald-600 font-medium ml-auto" data-id="element-3408">
                                Risparmio: {finding.savings}
                              </span>
                            </div>
                            <p className="text-[13px] text-[#1A202C] mt-1.5" data-id="element-3409">
                              {finding.desc}
                            </p>
                            <p className="text-[11px] text-gray-500 mt-1 flex items-center gap-1" data-id="element-3410">
                              <Leaf className="w-3 h-3" data-id="element-3411" /> {finding.rule}
                            </p>
                          </div>
                        </div>
                      </div>;
            })}
                </div>
              </div>
            </div>}

          {/* Scans Table - always visible */}
          <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden" data-id="element-3412">
            <div className="px-6 py-5 border-b border-gray-100" data-id="element-3413">
              <h2 className="text-[16px] font-semibold text-[#1A202C]" data-id="element-3414">
                Storico Scansioni
              </h2>
            </div>
            <div className="overflow-x-auto" data-id="element-3415">
              <table className="w-full text-left border-collapse" data-id="element-3416">
                <thead data-id="element-3417">
                  <tr className="bg-gray-50/50 border-b border-gray-100" data-id="element-3418">
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-3419">
                      Servizio
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-3420">
                      Repository
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-3421">
                      Data Scansione
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-center" data-id="element-3422">
                      Impatto Alto
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-center" data-id="element-3423">
                      Impatto Medio
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-center" data-id="element-3424">
                      Impatto Basso
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-right" data-id="element-3425">
                      Risparmio Stimato
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-3426">
                      Stato
                    </th>
                    <th className="px-6 py-3 text-[11px] font-semibold text-gray-500 uppercase tracking-wider text-right" data-id="element-3427">
                      Azioni
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-3428">
                  {SCAN_HISTORY.map(scan => <Fragment key={scan.id} data-id="element-3429">
                      <tr className="hover:bg-gray-50/50 transition-colors group" data-id="element-3430">
                        <td className="px-6 py-4" data-id="element-3431">
                          <span className="text-[13px] font-medium text-[#2a3a54]" data-id="element-3432">
                            {scan.service}
                          </span>
                        </td>
                        <td className="px-6 py-4" data-id="element-3433">
                          <span className="text-[13px] text-gray-500 font-mono" data-id="element-3434">
                            {scan.repo}
                          </span>
                        </td>
                        <td className="px-6 py-4" data-id="element-3435">
                          <span className="text-[13px] text-gray-500" data-id="element-3436">
                            {scan.date}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center" data-id="element-3437">
                          <span className={`text-[13px] font-bold ${scan.high > 0 ? 'text-red-600' : 'text-gray-400'}`} data-id="element-3438">
                            {scan.high}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center" data-id="element-3439">
                          <span className={`text-[13px] font-bold ${scan.medium > 0 ? 'text-amber-600' : 'text-gray-400'}`} data-id="element-3440">
                            {scan.medium}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-center" data-id="element-3441">
                          <span className={`text-[13px] font-bold ${scan.low > 0 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-3442">
                            {scan.low}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right" data-id="element-3443">
                          <span className="text-[13px] font-medium text-emerald-600" data-id="element-3444">
                            {scan.co2Savings}
                          </span>
                        </td>
                        <td className="px-6 py-4" data-id="element-3445">
                          {getStatusBadge(scan.status)}
                        </td>
                        <td className="px-6 py-4 text-right" data-id="element-3446">
                          <button className="text-[12px] font-medium text-[#00B4D8] hover:text-[#0096b4] transition-colors" data-id="element-3447">
                            {scan.high > 0 || scan.medium > 0 ? 'Vedi Dettagli' : 'Report'}
                          </button>
                        </td>
                      </tr>
                      {/* Expanded row mock for one item to show findings pattern */}
                      {scan.id === 'sc2' && <tr className="bg-gray-50/30 border-b border-gray-100" data-id="element-3448">
                          <td colSpan={9} className="px-6 py-4" data-id="element-3449">
                            <div className="bg-white border border-red-100 rounded-lg p-4 shadow-sm" data-id="element-3450">
                              <h4 className="text-[12px] font-bold text-red-800 uppercase tracking-wider mb-3 flex items-center gap-2" data-id="element-3451">
                                <AlertCircle className="w-4 h-4" data-id="element-3452" /> Findings
                                Critici (2)
                              </h4>
                              <div className="flex flex-col gap-2" data-id="element-3453">
                                <div className="flex items-start gap-3 p-3 bg-red-50/50 rounded-md border border-red-100/50" data-id="element-3454">
                                  <div className="mt-0.5" data-id="element-3455">
                                    <FileText className="w-4 h-4 text-red-500" data-id="element-3456" />
                                  </div>
                                  <div className="flex-1" data-id="element-3457">
                                    <div className="flex justify-between items-start" data-id="element-3458">
                                      <p className="text-[13px] font-medium text-[#1A202C] font-mono" data-id="element-3459">
                                        src/prompts/evaluate.ts{' '}
                                        <span className="text-gray-400 font-normal" data-id="element-3460">
                                          line 142
                                        </span>
                                      </p>
                                      <span className="text-[11px] text-emerald-600 font-medium" data-id="element-3461">
                                        Risparmio: ~8 kg CO₂/mese
                                      </span>
                                    </div>
                                    <p className="text-[12px] text-gray-600 mt-1" data-id="element-3462">
                                      I prompt contengono istruzioni ripetute e
                                      contesto statico (~1.800 token) che
                                      potrebbe essere spostato in un template
                                      condiviso.
                                    </p>
                                    <p className="text-[11px] text-red-600 font-medium mt-1 flex items-center gap-1" data-id="element-3463">
                                      <Leaf className="w-3 h-3" data-id="element-3464" /> Best
                                      Practice: Prompt Engineering
                                    </p>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>}
                    </Fragment>)}
                </tbody>
              </table>
            </div>
          </div>
        </div>}
    </div>;
}